import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HONEYPOT_FIELD, Honeypot, submitContact } from "../../utils/publicContact";

// Shared by the quote request (/contacts, ns "Contract") and the job application (/job, ns "Job").
const FIELDS = [
  { name: "vorname", label: "Vorname", type: "text", autoComplete: "given-name" },
  { name: "nachname", label: "Nachname", type: "text", autoComplete: "family-name" },
  {
    name: "email",
    label: "E-Mail",
    type: "email",
    autoComplete: "email",
    inputMode: "email"
  },
  { name: "telefon", label: "Telefon", type: "tel", autoComplete: "tel", inputMode: "tel" },
  { name: "datum", label: "Datum", type: "date" },
  { name: "nachricht", label: "Nachricht", textarea: true }
];

// Form field -> field name expected by POST /public/contact.
const API_NAMES = {
  vorname: "firstName",
  nachname: "lastName",
  email: "email",
  telefon: "phone",
  datum: "date",
  nachricht: "message"
};

const EMPTY = FIELDS.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {});
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = values =>
  FIELDS.reduce((errors, { name, type }) => {
    const value = values[name].trim();
    if (!value || (type === "email" && !EMAIL_RE.test(value))) errors[name] = true;
    return errors;
  }, {});

const QuoteForm = ({ ns, showTitle = false }) => {
  const { t } = useTranslation();
  const formType = ns === "Job" ? "job" : "contact";
  const [formData, setFormData] = useState(EMPTY);
  const [honeypot, setHoneypot] = useState("");
  const [invalid, setInvalid] = useState({});
  const [status, setStatus] = useState({ loading: false, error: "", success: false });
  const successTimer = useRef(null);
  const idPrefix = `${ns.toLowerCase()}-form`;
  const errorId = `${idPrefix}-error`;

  useEffect(() => () => clearTimeout(successTimer.current), []);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (invalid[name]) setInvalid(prev => ({ ...prev, [name]: false }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const form = e.currentTarget;
    const errors = validate(formData);
    setInvalid(errors);

    if (Object.keys(errors).length) {
      setStatus({ loading: false, error: t(`${ns}.errorAllFields`), success: false });
      form.querySelector(`[name="${Object.keys(errors)[0]}"]`)?.focus();
      return;
    }

    setStatus({ loading: true, error: "", success: false });

    try {
      const payload = { site: "construction", formType, [HONEYPOT_FIELD]: honeypot };
      FIELDS.forEach(({ name }) => {
        payload[API_NAMES[name]] = formData[name].trim();
      });
      await submitContact(payload);
      setStatus({ loading: false, error: "", success: true });
      setFormData(EMPTY);
      successTimer.current = setTimeout(
        () => setStatus(prev => ({ ...prev, success: false })),
        5000
      );
    } catch (err) {
      const serverInvalid = FIELDS.reduce((acc, { name }) => {
        if (err.fieldErrors?.[API_NAMES[name]]) acc[name] = true;
        return acc;
      }, {});
      const hasFieldErrors = Object.keys(serverInvalid).length > 0;
      let error = t(`${ns}.errorSend`);
      if (hasFieldErrors) error = t(`${ns}.errorInvalidFields`);
      else if (err.status === 429) error = t(`${ns}.errorTooMany`);
      setInvalid(serverInvalid);
      setStatus({ loading: false, error, success: false });
      if (hasFieldErrors) form.querySelector(`[name="${Object.keys(serverInvalid)[0]}"]`)?.focus();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="php-email-form quote-form" noValidate>
      {showTitle && <h3>{t(`${ns}.formTitle`)}</h3>}
      <p>{t(`${ns}.formDesc`)}</p>

      <div className="row gy-3">
        <Honeypot value={honeypot} onChange={e => setHoneypot(e.target.value)} />

        {FIELDS.map(({ name, label, textarea, ...inputProps }) => {
          const id = `${idPrefix}-${name}`;
          const common = {
            id,
            name,
            className: `form-control ${invalid[name] ? "is-invalid" : ""}`,
            required: true,
            "aria-required": true,
            "aria-invalid": invalid[name] || undefined,
            "aria-describedby": invalid[name] ? errorId : undefined,
            value: formData[name],
            onChange: handleChange
          };
          return (
            <div className="col-12" key={name}>
              <label htmlFor={id} className="quote-form__label">
                {t(`${ns}.fields.${label}`)}
                <span className="quote-form__required" aria-hidden="true">
                  {" *"}
                </span>
              </label>
              {textarea ? <textarea {...common} rows="6" /> : <input {...common} {...inputProps} />}
            </div>
          );
        })}

        <div className="col-12 text-center">
          <div aria-live="polite">
            {status.loading && <div className="loading">{t("Loading")}</div>}
            {status.success && <div className="sent-message">{t(`${ns}.successMessage`)}</div>}
          </div>
          {status.error && (
            <div id={errorId} className="error-message" role="alert">
              {status.error}
            </div>
          )}

          <button type="submit" disabled={status.loading} aria-busy={status.loading || undefined}>
            {t("getStarted.submit")}
          </button>
        </div>
      </div>
    </form>
  );
};

export default QuoteForm;
